/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KONTENT DİLİ — telefon/planşet app-ları ilə EYNİ MODEL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sahib tələbi (2026-08-03): «tv projelərin üçündə də profil bölümündə dil
 * seçimi olsun applar kimi». Android TV-də artıq vardı, burada və tvOS-da yox idi.
 *
 * ⚠ NƏ DƏYİŞİR: YALNIZ API-dən gələn məzmun (film adı, təsvir, janr) — sorğulara
 *   `lang=<kod>` əlavə olunur. App-ın ÖZ düymələri («Profil», «İzlə») Azərbaycanca
 *   qalır; eyni qərar iOS `AppSettings` və Android `AppSettings`-də də verilib.
 *
 * ⚠ NİYƏ AYRI FAYL (`session.ts`-in içində deyil): dil seçimi GİRİŞDƏN ASILI
 *   DEYİL — anonim istifadəçi də seçə bilir və çıxış onu sıfırlamamalıdır.
 *
 * ⚠ `localStorage` TRY/CATCH-DƏDİR: bəzi TV brauzerlərində (webOS 3 gizli rejim,
 *   Tizen bəzi modellər) `localStorage`-a müraciət ATIR. `session.ts` ilə eyni qayda.
 */

/** Dəstəklənən dillər — iOS `AppSettings.availableLanguages` ilə eyni siyahı və sıra. */
export const LANGUAGES: Array<{ code: string; native: string }> = [
  { code: 'az', native: 'Azərbaycan' },
  { code: 'en', native: 'English' },
  { code: 'ru', native: 'Русский' },
  { code: 'tr', native: 'Türkçe' },
  { code: 'de', native: 'Deutsch' },
]

const STORAGE_KEY = 'kb_language'
const DEFAULT_LANGUAGE = 'az'

/** Prosesin cari dəyəri — hər sorğuda `localStorage` oxunmasın deyə keşlənir. */
let current = read()

function read(): string {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_LANGUAGE
    for (let i = 0; i < LANGUAGES.length; i++) {
      // ⚠ Ağ siyahı yoxlaması: saxlancda zibil dəyər olsa (əl ilə redaktə, köhnə
      //   versiya) API `lang=xx` alıb boş kataloq qaytarardı.
      if ((LANGUAGES[i] as { code: string }).code === raw) return raw
    }
    return DEFAULT_LANGUAGE
  } catch {
    return DEFAULT_LANGUAGE
  }
}

/** Dil saxlancda VARMI — ilk açılış dilinin (BR 26.4) yeganə şərti. */
export function hasStoredLanguage(): boolean {
  try {
    return !!window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // Saxlanc bağlıdır — hər açılış «ilk»dir, amma yazılış da alınmayacaq; soruşmuruq.
    return true
  }
}

/** Cari kontent dili (`az` · `en` · `ru` · `tr` · `de`). */
export function readLanguage(): string {
  return current
}

/** Dili yazır. Dəyər dəyişməyibsə heç nə etmir. */
export function writeLanguage(code: string): void {
  if (code === current) return
  current = code
  syncDocumentLanguage()
  try {
    window.localStorage.setItem(STORAGE_KEY, code)
  } catch {
    // Saxlanc bağlıdır — seçim yalnız bu sessiyada qalır, app sınmır.
  }
}

/**
 * Dili HƏMİŞƏ saxlanca yazır — dəyər defolt ilə eyni olsa da (ilk açılış dili, BR 26.4).
 * `writeLanguage` eyni dəyərdə heç nə yazmır; ilk açılışda isə məhz «seçim edildi» qeydi
 * lazımdır, yoxsa coğrafi sorğu hər açılışda təkrarlanardı.
 */
export function rememberLanguage(code: string): void {
  current = code
  syncDocumentLanguage()
  try {
    window.localStorage.setItem(STORAGE_KEY, code)
  } catch {
    // Saxlanc bağlıdır — seçim yalnız bu sessiyada qalır.
  }
}

/**
 * `<html lang>` atributunu cari dillə eyniləşdirir.
 *
 * ⚠ NİYƏ LAZIMDIR (2026-08-16 defekti — TestingClaude veb TV 42). ÖLÇÜLDÜ:
 *   dil dörd dəfə dəyişdirildi (en · ru · tr), `document.documentElement.lang`
 *   HƏR DƏFƏ «az» qaldı — çünki atribut yalnız `browser.html`/`index.html`-də
 *   sabit yazılıb və heç yerdə yenilənmirdi. Nəticəsi görünməzdir, amma realdır:
 *   TV brauzerinin mətn-oxuma və defis qaydaları, həmçinin `:lang()` seçiciləri
 *   səhv dilə baxır.
 * ⚠ Boot-da da çağırılır (`main.ts`) — saxlancda köhnə seçim varsa atribut
 *   ilk kadrdan düzgün olsun.
 * ⚠ `document` yoxlanılır: modul vahid testlərində DOM olmaya bilər.
 */
export function syncDocumentLanguage(): void {
  if (typeof document === 'undefined' || !document.documentElement) return
  document.documentElement.lang = current
}

/** Dilin öz adı (`ru` → `Русский`). Tanınmasa kodun özü böyük hərflə. */
export function languageNative(code: string): string {
  for (let i = 0; i < LANGUAGES.length; i++) {
    const item = LANGUAGES[i] as { code: string; native: string }
    if (item.code === code) return item.native
  }
  return code.toUpperCase()
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DİL DƏYİŞƏNDƏ BOOT KEŞİNİN YENİDƏN ÇƏKİLMƏSİ (2026-08-30)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠⚠ NƏ ÖLÇÜLDÜ: ana ekranın datası (`home` · «Sənin üçün» · «Davam et» ·
 *    «Sonra İzlə») BOOT-DA BİR DƏFƏ çəkilir və giriş nöqtəsinin modul dəyişənində
 *    saxlanılır (`main.ts` / `browser/main.ts` → `Promise.all`). Dil dəyişəndə
 *    ekran yenidən qurulurdu, AMMA həmin keşdən — yəni film adları, blok
 *    başlıqları və janrlar KÖHNƏ DİLDƏ qalırdı; yalnız tam yeniləmə (F5 / app-ın
 *    yenidən açılması) düzəldirdi. Statik mətnlər tərcümə olunduğu üçün ekran
 *    «yarısı tərcümə olunmuş» görünürdü — sahibin şikayəti məhz budur.
 *
 * ⚠ NİYƏ QEYDİYYAT, BİRBAŞA İMPORT DEYİL: yenidən çəkən funksiya giriş
 *   nöqtəsindədir (keş onun modul vəziyyətidir), çağıran isə `browser/shell.ts`
 *   və `screens/profile.ts`-dir — onları giriş nöqtəsi ONSUZ DA import edir, yəni
 *   birbaşa import DÖVRƏ yaradardı. Kod bazasının öz naxışı budur:
 *   `setDetailOpener`, `onRouteChange`, `onWatchlistChange`.
 * ⚠ İKİ HƏDƏF, İKİ AYRI BUNDLE: hər birində yalnız ÖZ giriş nöqtəsi qeydiyyatdan
 *   keçir, ona görə tək xana bəs edir.
 * ⚠ QEYDİYYAT OLMASA (vahid testlər) sakitcə həll olunmuş promise qayıdır.
 */
let languageReloader: (() => Promise<void>) | null = null

/** Giriş nöqtəsi öz boot keşini yeniləyən funksiyanı burada qeyd edir. */
export function onLanguageChange(handler: () => Promise<void>): void {
  languageReloader = handler
}

/**
 * Dil seçildikdən SONRA, ekran yenidən qurulmazdan ƏVVƏL çağırılır.
 * Qaytarılan promise data gələndə həll olunur — çağıran onu gözləmək MƏCBUR
 * deyil (gözləsə ekran şəbəkə qədər donardı); yenidən qurma qeydiyyatdan keçmiş
 * funksiyanın öz işidir.
 */
export function reloadForLanguage(): Promise<void> {
  return languageReloader ? languageReloader() : Promise.resolve()
}

/**
 * Sorğuya dil parametri əlavə edir: `/api/catalog/home` → `…?lang=ru`.
 * Ünvanda artıq `?` varsa `&` işlədilir.
 */
export function withLanguage(path: string): string {
  return path + (path.indexOf('?') >= 0 ? '&' : '?') + 'lang=' + current
}
